'use client';

/**
 * RuntimeContextProvider — React context wrapper for RuntimeContextCollector.
 *
 * Initializes the collector on mount (SSR-safe) and tears it down on unmount.
 * When NEXT_PUBLIC_DEBUG_CHAT_ENABLED is not 'true', renders children with no patching.
 */

import { createContext, useEffect, useRef, type ReactNode } from 'react';
import { RuntimeContextCollector } from './RuntimeContextCollector';
import type { RuntimeContext, WebSocketEvent } from './types';

// ---------------------------------------------------------------------------
// Context types
// ---------------------------------------------------------------------------

export interface RuntimeContextValue {
  getContext: () => RuntimeContext;
  reportWsEvent: (event: WebSocketEvent) => void;
  reportError: (error: Error, componentStack?: string, sentryEventId?: string) => void;
}

const RuntimeContext = createContext<RuntimeContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface RuntimeContextProviderProps {
  children: ReactNode;
}

export function RuntimeContextProvider({ children }: RuntimeContextProviderProps) {
  // Check feature flag — if not enabled, render children without patching
  const enabled =
    typeof process !== 'undefined' &&
    process.env?.NEXT_PUBLIC_DEBUG_CHAT_ENABLED === 'true';

  if (!enabled) {
    return <>{children}</>;
  }

  return <ActiveRuntimeContextProvider>{children}</ActiveRuntimeContextProvider>;
}

/**
 * Inner provider that actually initializes the collector.
 * Only rendered when the feature flag is enabled.
 */
function ActiveRuntimeContextProvider({ children }: RuntimeContextProviderProps) {
  const collectorRef = useRef<RuntimeContextCollector | null>(null);

  // We store a stable value reference so consumers don't re-render on every call
  const valueRef = useRef<RuntimeContextValue>({
    getContext: () => collectorRef.current?.getContext() ?? emptyContext(),
    reportWsEvent: (event) => collectorRef.current?.reportWsEvent(event),
    reportError: (error, componentStack, sentryEventId) =>
      collectorRef.current?.reportError(error, componentStack, sentryEventId),
  });

  useEffect(() => {
    // SSR safety — only initialize in browser
    if (typeof window === 'undefined') return;

    collectorRef.current = new RuntimeContextCollector();

    return () => {
      collectorRef.current?.destroy();
      collectorRef.current = null;
    };
  }, []);

  return (
    <RuntimeContext.Provider value={valueRef.current}>
      {children}
    </RuntimeContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function emptyContext(): RuntimeContext {
  return {
    consoleLogs: [],
    networkRequests: [],
    wsEvents: [],
    errors: [],
    performance: [],
    appState: {
      wsConnectionStatus: 'unknown',
      currentRoute: '/',
      walletConnected: false,
      visibleDialogs: [],
      screenSize: { width: 0, height: 0 },
      timestamp: Date.now(),
    },
    environment: {
      userAgent: 'unknown',
      buildId: 'unknown',
      dataEnv: 'unknown',
      nextPublicApiUrl: '',
      sentryEnabled: false,
      timestamp: Date.now(),
    },
  };
}

// ---------------------------------------------------------------------------
// Raw context export for the hook
// ---------------------------------------------------------------------------

export { RuntimeContext };
